<template>
  <Teleport to="body">
    <Transition name="site-prefs-bar">
      <div
        v-if="store.bannerVisible"
        class="site-prefs-bar site-prefs-bar--layer fixed inset-x-0 bottom-0 z-[10040] px-3 pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] pointer-events-none"
        role="region"
        aria-label="Site preferences"
        data-testid="cookie-consent-bar"
      >
        <div
          class="pointer-events-auto max-w-3xl mx-auto rounded-2xl border border-slate-200 dark:border-zinc-700 bg-white/95 dark:bg-zinc-900/95 backdrop-blur-md shadow-xl px-4 py-4 sm:px-5 sm:py-4"
        >
          <p class="text-sm font-semibold text-charcoal dark:text-white mb-1">
            {{ t('public.cookieConsent.barTitle') }}
          </p>
          <p class="text-xs sm:text-sm text-slate-600 dark:text-slate-400 leading-relaxed mb-4">
            {{ t('public.cookieConsent.barBody') }}
            <button
              type="button"
              class="text-primary font-semibold underline underline-offset-2 min-h-[44px] sm:min-h-0 inline-flex items-center px-1 -mx-1"
              @click="legalPrivacyOpen = true"
            >
              {{ t('public.cookieConsent.privacyLink') }}
            </button>.
          </p>
          <div class="flex flex-col sm:flex-row gap-2 sm:gap-3 sm:flex-wrap sm:items-center">
            <button
              type="button"
              class="min-h-[44px] px-4 rounded-xl bg-primary text-white font-semibold text-sm order-1 sm:order-none"
              data-testid="cookie-consent-accept-all"
              @click="store.acceptAll()"
            >
              {{ t('public.cookieConsent.acceptAll') }}
            </button>
            <button
              type="button"
              class="min-h-[44px] px-4 rounded-xl border border-slate-300 dark:border-zinc-600 text-charcoal dark:text-white font-semibold text-sm order-2 sm:order-none"
              @click="store.rejectNonEssential()"
            >
              {{ t('public.cookieConsent.rejectNonEssential') }}
            </button>
            <button
              type="button"
              class="min-h-[44px] px-4 rounded-xl text-primary font-semibold text-sm order-3 sm:order-none sm:ml-auto"
              @click="onCustomize"
            >
              {{ t('public.cookieConsent.customize') }}
            </button>
          </div>
        </div>
      </div>
    </Transition>
    <LegalContentModal
      :open="legalPrivacyOpen"
      type="privacy"
      :locale="legalLocale"
      @close="legalPrivacyOpen = false"
    />
  </Teleport>
</template>

<script setup>
import { ref, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useCookieConsentStore } from '@/stores/sitePrefs'
import LegalContentModal from '@/components/legal/LegalContentModal.vue'

const { t, locale } = useI18n()
const store = useCookieConsentStore()
const legalPrivacyOpen = ref(false)

/** Legal API supports en, es, ar; fall back to en for other UI locales. */
const legalLocale = computed(() => {
  const l = locale.value
  return l === 'es' || l === 'ar' ? l : 'en'
})

function onCustomize() {
  store.openPrefs()
}
</script>

<style scoped>
.site-prefs-bar-enter-active,
.site-prefs-bar-leave-active {
  transition: opacity 0.25s ease, transform 0.25s ease;
}
.site-prefs-bar-enter-from,
.site-prefs-bar-leave-to {
  opacity: 0;
  transform: translateY(12px);
}
</style>

<!-- Unscoped: offset above PublicRestaurantView owner sticky + mobile view-menu bar -->
<style>
/*
  Owner UI (PublicRestaurantView): .rms-view-menu-bar bottom 0 (~56px); .rms-owner-sticky mobile bottom 60px; desktop bottom 0 centered pill.
  Lift consent bar so it does not cover "viewing as owner" / "view as customer" controls.
*/
html[data-rms-public-owner-ui='sticky'] .site-prefs-bar--layer {
  bottom: calc(5.5rem + env(safe-area-inset-bottom, 0px));
}
@media (max-width: 767px) {
  html[data-rms-public-owner-ui='sticky'] .site-prefs-bar--layer {
    bottom: calc(60px + 4.75rem + env(safe-area-inset-bottom, 0px));
  }
}
html[data-rms-public-owner-ui='minimal'] .site-prefs-bar--layer {
  bottom: calc(3.75rem + env(safe-area-inset-bottom, 0px));
}
@media (max-width: 767px) {
  html[data-rms-public-owner-ui='minimal'] .site-prefs-bar--layer {
    bottom: calc(60px + 3.5rem + env(safe-area-inset-bottom, 0px));
  }
}
</style>
